import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Modal, Button } from 'react-bootstrap';

const LegalDocument = ({ show, onHide, document }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const documentInfo = {
    'terms': {
      title: 'Terms of Service',
      file: '/legal/terms-of-service.md'
    },
    'privacy': {
      title: 'Privacy Policy',
      file: '/legal/privacy-policy.md'
    },
    'subscription': {
      title: 'Subscription & Refund Policy',
      file: '/legal/subscription-policy.md'
    },
    'disclaimers': {
      title: 'Disclaimers and Warnings',
      file: '/legal/disclaimers.md'
    }
  };

  useEffect(() => {
    if (!show || !document) return;

    const fetchDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const docInfo = documentInfo[document];
        if (!docInfo) {
          throw new Error('Unknown document');
        }

        const response = await fetch(docInfo.file);
        if (!response.ok) {
          throw new Error('Failed to load document');
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [show, document]);

  if (!show) return null;

  const currentDoc = documentInfo[document] || {};

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="legal-document-modal"
    >
      <Modal.Header
        closeButton
        style={{
          background: '#1e293b',
          borderBottom: '1px solid #334155',
          color: '#fff'
        }}
      >
        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: '600' }}>
          {currentDoc.title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          background: '#0f172a',
          color: '#cbd5e1',
          maxHeight: '70vh',
          overflowY: 'auto'
        }}
      >
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(59, 130, 246, 0.3)',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}
            />
            <p style={{ color: '#94a3b8' }}>Loading document...</p>
          </div>
        )}

        {error && (
          <div
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center'
            }}
          >
            <p style={{ color: '#ef4444', marginBottom: '0' }}>
              Error loading document: {error}
            </p>
          </div>
        )}

        {!loading && !error && (
          <div className="legal-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom styling for markdown elements
                h1: ({ node, ...props }) => (
                  <h1 style={{ color: '#fff', fontSize: '2rem', marginTop: '1.5rem', marginBottom: '1rem', borderBottom: '2px solid #334155', paddingBottom: '0.5rem' }} {...props} />
                ),
                h2: ({ node, ...props }) => (
                  <h2 style={{ color: '#fff', fontSize: '1.5rem', marginTop: '1.5rem', marginBottom: '0.75rem' }} {...props} />
                ),
                h3: ({ node, ...props }) => (
                  <h3 style={{ color: '#e2e8f0', fontSize: '1.25rem', marginTop: '1.25rem', marginBottom: '0.5rem' }} {...props} />
                ),
                h4: ({ node, ...props }) => (
                  <h4 style={{ color: '#e2e8f0', fontSize: '1.1rem', marginTop: '1rem', marginBottom: '0.5rem' }} {...props} />
                ),
                p: ({ node, ...props }) => (
                  <p style={{ lineHeight: '1.7', marginBottom: '1rem', color: '#cbd5e1' }} {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem', color: '#cbd5e1' }} {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem', color: '#cbd5e1' }} {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li style={{ marginBottom: '0.5rem', lineHeight: '1.6' }} {...props} />
                ),
                a: ({ node, ...props }) => (
                  <a style={{ color: '#3b82f6', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props} />
                ),
                strong: ({ node, ...props }) => (
                  <strong style={{ color: '#fff', fontWeight: '600' }} {...props} />
                ),
                hr: ({ node, ...props }) => (
                  <hr style={{ border: 'none', borderTop: '1px solid #334155', margin: '2rem 0' }} {...props} />
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote style={{ borderLeft: '4px solid #3b82f6', paddingLeft: '1rem', margin: '1rem 0', color: '#94a3b8', fontStyle: 'italic' }} {...props} />
                ),
                code: ({ node, inline, ...props }) => (
                  inline ? (
                    <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em', color: '#e2e8f0' }} {...props} />
                  ) : (
                    <code style={{ display: 'block', background: '#1e293b', padding: '1rem', borderRadius: '8px', overflowX: 'auto', fontSize: '0.9em', color: '#e2e8f0' }} {...props} />
                  )
                ),
                table: ({ node, ...props }) => (
                  <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #334155' }} {...props} />
                  </div>
                ),
                thead: ({ node, ...props }) => (
                  <thead style={{ background: '#1e293b' }} {...props} />
                ),
                th: ({ node, ...props }) => (
                  <th style={{ padding: '0.75rem', border: '1px solid #334155', color: '#fff', fontWeight: '600', textAlign: 'left' }} {...props} />
                ),
                td: ({ node, ...props }) => (
                  <td style={{ padding: '0.75rem', border: '1px solid #334155', color: '#cbd5e1' }} {...props} />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer
        style={{
          background: '#1e293b',
          borderTop: '1px solid #334155'
        }}
      >
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LegalDocument;
